/* eslint-disable react/jsx-no-useless-fragment */
import _ from 'underscore';
import React, { useCallback, useContext, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import classnames from 'classnames';
import {
  MessageProps, MessageThread, SendBox, MessageRenderer,
} from '@azure/communication-react';
import {
  Stack, StackItem, PrimaryButton, DefaultButton, TooltipHost, MessageBarType, ActionButton, Toggle, DirectionalHint,
} from '@fluentui/react';
import { useMsal } from '@azure/msal-react';

import { ChatManagerContext } from './Components/ChatManagerContext';
import { WelcomeMessage } from './Components/WelcomeMessage';
import { WelcomeTopicsPage, TopicList } from './Components/WelcomeTopics';
import { Topic } from './Components/DeveloperPanel';
import {
  restartIcon, sendIcon, restartIconWithoutBg, sendIconVivara,
} from './Assets';
import { AdMessageWithActions, ChatMessageWithActions } from './Components/CustomMessage';
import { ChatMessage, IMultiDeploymentChatHistory } from './Components/CustomMessage/message-types';
import { TodoEditor } from './Components/DailyPlanner/TodoEditor';
import { MARKDOWN_PREFIX } from './Components/DailyPlanner/common';
import { useLLMSetting } from './Components/Contexts';
import { Fireworks } from './Fireworks';
import { MarkdownMessage } from './Components/CustomMessage/markdown-message';
import { useIsVivara } from './hooks/useIsVivara';
import { useIsDevMode } from './hooks/useIsDevMode';
import { LLMModel } from './Components/Contexts/LLMSettingContext';
import { DailyPlanner } from './Components/DailyPlanner';
import { PlaygroundTheme } from './Theme';
import { useNotificationApi } from './Components/NotificationApi';
import { useInviteCode } from './Repository/InviteCode';

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  messageThreadWrapper: {
    flex: 1,
    overflow: 'auto',

    // hide scrollbar
    scrollbarWidth: 'none', /* Firefox */
    '-ms-overflow-style': 'none', /* IE 10+ */
    '& ::-webkit-scrollbar': {
      display: 'none', /* Chrome Safari */
    },

    '& ul': {
      background: 'none',
      paddingBottom: '15px',

      '& .ui-chat__item .ui-chat__item__message': {
        '@media (width <= 600px)': {
          maxWidth: '90%',
        },
      },
    },
  },
  sendBoxWrapper: {},
  title: {
    padding: '0 20px',
  },
  topicTitle: {
    display: 'flex',
    flexDirection: 'row',
    textAlign: 'center',
    alignItems: 'center',
    borderBottom: '1px solid var(--lin, #D6D6D6)',
    '& .backTopicButton': {
      fontSize: '14px',
      '& .ms-Button-textContainer': {
        '@media (width <= 600px)': {
          display: 'none',
        },
      },
      '& .ms-Button-icon': {
        color: theme.themeDarkAlt,
      },
      '&:hover': {
        color: theme.themeDarkAlt,
      },
    },
    '& .topicTitleText': {
      flex: '1 1 auto',
      fontWeight: 600,
      fontSize: '16px',
    },
    '& .autoSaveToggle': {
      margin: '0px',
      '& .ms-Toggle-label': {
        fontWeight: 'normal',
        marginRight: '8px',
      },
      '& .ms-Toggle-background': {
        width: '44px',
      },
      '& .ms-Toggle-stateText': {
        alignContent: 'center',
        cursor: 'pointer',
        margin: '0px 20px 0px -26px',
        zIndex: 1,
      },
      '&.is-checked': {
        '& .ms-Toggle-background': {
          backgroundColor: theme.themePrimary,
          '&:hover': {
            backgroundColor: theme.themeLight,
          },
        },
        '& .ms-Toggle-stateText': {
          color: 'white',
          margin: '0px 32px 0px -38px',
        },
      },
    },
  },
  messageBar: {
    margin: '20px 0 80px 0',

    '& .ms-StackItem .ms-Stack': {
      borderRadius: '30px',
      borderColor: 'white',
    },
    '& .ms-StackItem .ms-Stack:hover, .ms-Stack:focus': {
      borderColor: theme.themePrimary,
    },
    '&.is-vivara': {
      margin: '30px 20px',
      padding: 0,
      '& .ms-StackItem .ms-Stack': {
        borderRadius: '8px',
        borderColor: '#d8d8d8',
      },
    },
  },
  narrowButtonGroup: {
    display: 'flex',

    '& svg': {
      marginRight: '15px',
      cursor: 'pointer',
    },

    '& svg:hover': {
      scale: '1.1',
    },
  },
  restartIconVivara: {
    display: 'flex',
    background: `linear-gradient(322.65deg, ${theme.themePrimary} 14.1%, ${theme.themeLighter} 85.15%)`,
    alignItems: 'center',
    padding: 12,
    gap: 9,
    borderRadius: '100px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: 16,
    width: 48,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    transition: 'width 0.3s',
    '& svg': {
      marginRight: '0',
      flexShrink: 0,
      '&:hover': {
        scale: 1,
      },
    },
    '& span': {
      display: 'none',
    },
    '&:hover': {
      width: 132,
      '& span': {
        display: 'inline-block',
      },
    },
  },
  sendBoxContainer: {
    position: 'relative',
    '& >.ms-Stack': {
      margin: 0,
    },
  },
  vivaraTaskIcon: {
    position: 'absolute',
    top: '50%',
    right: '55px',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    '& button': {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      color: theme.neutralDark,
      '&:hover': {
        color: theme.themeLight,
      },
    },
    '& .split-line': {
      width: '1px',
      height: '24px',
      backgroundColor: '#E0E0E0',
    },
  },
  sendLogButton: {
    '&.is-vivara': {
      height: 48,
      backgroundColor: theme.themePrimary,
      borderColor: theme.themePrimary,
      padding: '0 8px',
      fontSize: 16,
      '&:hover': {
        backgroundColor: theme.themeDarkAlt,
        borderColor: theme.themeDarkAlt,
      },
    },
  },
  copyHistoryButton: {
    '&.is-vivara': {
      height: 48,
      backgroundColor: theme.themeLight,
      borderColor: theme.themeLight,
      padding: '0 8px',
      fontSize: 16,
      '&:hover': {
        backgroundColor: theme.themeDarkAlt,
        borderColor: theme.themeDarkAlt,
      },
      '@media (width <= 600px)': {
        display: 'none',
      },
    },
  },
  toggleDeploymentNameButton: {
    '&.is-vivara': {
      height: 48,
      padding: '0 8px',
      fontSize: 16,
    },
  },
}));

const messageThreadStyles = {
  root: {
    padding: '0 20px',
    fontSize: '18px',
    lineHeight: '1.5',

    '& .ui-chat__message': {
      padding: '20px',
      borderRadius: '15px',
      boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
    },

    '& .ui-chat__message__author, .ui-chat__item__gutter': {
      display: 'none',
    },

    '& .ui-chat__messageheader': {
      marginBottom: '5px',

      '& span': {
        fontSize: '16px',
      },
    },

    '& .ui-chat__messagecontent': {
      '& .deployment-name': {
        color: 'green',
      },
      '& .video-content': {
        width: '100%',
        minHeight: '315px',
        midWidth: '560px',
      },

    },

    '& .ui-chat__message__actions': {
      padding: '10px',
      margin: 0,
      width: 'auto',
      minWidth: '92px',
      zIndex: 10000,
      listStyle: 'none',
      backgroundColor: 'white',
      boxShadow: '0px 0.3px 10.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
      borderRadius: '5px',
      display: 'flex',

      '& a.ui-menu__item': {
        display: 'inline-block',
        float: 'left',
        padding: 0,
        margin: 0,
      },

      '& button.menuButton': {
        verticalAlign: 'middle',
      },
    },

    '& .feedback-show-container': {
      float: 'right',
      marginTop: '10px',
      width: '45px',
      height: '35px',
      backgroundColor: '#F9F9F9',
      boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
      borderRadius: '20px',
      padding: '4px 4px 4px 4px',
      border: '1px solid #A2B7F4',
      '& .likeButton': {
        height: '25px',
        visibility: 'hidden',
        '& svg': {
          height: '25px',
          width: '25px',
          visibility: 'visible',
          cursor: 'pointer',
        },
      },
      '& .dislikeButton': {
        height: '25px',
        visibility: 'hidden',
        '& svg': {
          height: '25px',
          width: '25px',
          visibility: 'visible',
          cursor: 'pointer',
        },
      },
    },

    '& .ad-custom-message-container': {
      maxWidth: '70%',
      minWidth: '720px',
      margin: 0,

      '& .ui-chat__message': {
        background: 'rgba(255, 255, 255, 0.8)',
      },
    },
  },

  chatItemMessageContainer: {
    maxWidth: '70%',
    margin: 0,

    '& .ui-chat__message': {
      background: 'rgba(255, 255, 255, 0.8)',
    },

    '& .ui-chat__messageheader': {
      marginBottom: '5px',

      '& span': {
        fontSize: '16px',
      },
    },

    '& .ui-flex': {
      display: 'inline-block',
    },
  },

  myChatItemMessageContainer: {
    maxWidth: '70%',
    margin: 0,

    '& .ui-chat__message': {
      background: 'rgba(54, 136, 215, 0.9)',
    },

    '& .ui-chat__messageheader': {
      '& span': {
        color: 'white',
        '-webkit-text-fill-color': 'white',
      },
    },

    '& .ui-chat__message__content': {
      '& div': {
        color: 'white',
        '-webkit-text-fill-color': 'white',
      },
    },
  },
};

const sendBoxStyle = (isVivara: boolean) => ({
  textField: {
    '& .ms-TextField-fieldGroup': {
      overflow: 'hidden',
    },

    '& #sendbox': {
      padding: 0,
      overflow: 'auto',
      fontSize: isVivara ? '16px' : '18px',
      lineHeight: '36px',
    },

    // styles for placeholder text
    '& textarea::-webkit-input-placeholder': {
      /* WebKit browsers */
      fontSize: isVivara ? '16px' : '18px',
    },
    '& textarea:-moz-placeholder': {
      /* Mozilla Firefox 4 to 18 */
      fontSize: isVivara ? '16px' : '18px',
    },
    '& textarea::-moz-placeholder': {
      /* Mozilla Firefox 19+ */
      fontSize: isVivara ? '16px' : '18px',
    },
    '& textarea::-ms-input-placeholder': {
      /* Internet Explorer 10+ */
      fontSize: isVivara ? '16px' : '18px',
    },
  },

  textFieldContainer: {
    padding: isVivara ? '6px 60px 6px 16px' : '15px 60px 15px 30px',
    backgroundColor: 'white',
    borderRadius: '30px',
  },

  sendMessageIconContainer: {
    marginRight: '20px',
  },
});

export const ChatPage: React.FunctionComponent = () => {
  const {
    messages,
    shownMessage,
    sendMessage,
    restartConversation,
    resetTopics,
    isPendingUserFeedback,
    toggleBestResponse,
    isMultiDeploymentExperiment,
    saveConversationHistory,
    getConversaionHistory,
    isShowDeploymentName,
    setIsShowDeploymentName,
    isTriggerFireworks,
    setIsTriggerFireworks,
    collectRate,
    collectAnnotations,
  } = useContext(ChatManagerContext)!;
  const {
    autoSave,
    conversationId,
    model,
    selectedTopic,
    update,
  } = useLLMSetting();
  const { inviteCode, sendInviteCode } = useInviteCode();
  const onLikeHandler = useCallback((message: ChatMessage, liked: boolean) => {
    toggleBestResponse(message, liked);
  }, [toggleBestResponse]);
  const isVivara = useIsVivara();
  const isDevMode = useIsDevMode();
  const classes = useStyles();

  const renderMessage = useCallback((messageProps: MessageProps, messageRenderer?: MessageRenderer) => {
    // welcome message at the beginning of the chat
    if (messageProps.message.messageType === 'custom' && messageProps.message.content === 'welcome') {
      if (isVivara) {
        if (selectedTopic !== Topic.None) {
          return <></>;
        }
        // eslint-disable-next-line max-len
        return <WelcomeMessage title="Chat with Vivara" subTitle="Vivara, a name representing Vivacity (Happiness), Vitality (Health),  Value (Productive)" />;
      }
      // eslint-disable-next-line max-len
      return <WelcomeMessage subTitle={isMultiDeploymentExperiment ? 'NOTE: TO ENSURE THE ACCURACY OF THE EXPERIMENT, PLEASE DO NOT UPDATE "LIKE" STATUSES OF THE MESSAGES ONCE SELECTED' : undefined} />;
    }

    // no actions for client message
    if (messageProps.message.messageType === 'chat' && messageProps.message.mine) {
      if (isVivara) {
        if (typeof messageProps.message.content === 'string' && messageProps.message.content.startsWith(MARKDOWN_PREFIX)) {
          return (
            <MarkdownMessage messageProps={messageProps} isUserMessage />
          );
        }
        return (
          <ChatMessageWithActions
            message={messageProps.message}
            isUserMessage
          />
        );
      } if (model === LLMModel.Companion) {
        if (typeof messageProps.message.content === 'string' && messageProps.message.content.startsWith(MARKDOWN_PREFIX)) {
          return (
            <MarkdownMessage messageProps={messageProps} />
          );
        }
      }
      return messageRenderer ? messageRenderer(messageProps) : <></>;
    }

    // show actions for bot chat message
    if (messageProps.message.messageType === 'chat') {
      const isCandidateSelectEndSet = new Set(messages.filter((msg) => _.has(msg, 'isCandidateSelectEnd')).map((msg) => msg.messageId));

      return (
        <ChatMessageWithActions
          message={messageProps.message}
          onLike={onLikeHandler}
          onDislike={(msg, disliked) => collectRate(messageProps.message as ChatMessage, disliked ? 'dislike' : '')}
          disableLike={isMultiDeploymentExperiment && isCandidateSelectEndSet.has(messageProps.message.messageId)}
          disableDislike={isMultiDeploymentExperiment}
          enableAnnotation={model === LLMModel.Companion}
          isShowDeploymentName={isShowDeploymentName}
          onAnnotation={collectAnnotations}
        />
      );
    }

    // show actions for bot custom ad message
    if (messageProps.message.messageType === 'custom') {
      return <AdMessageWithActions message={messageProps.message} />;
    }

    return <></>;
  }, [onLikeHandler, isMultiDeploymentExperiment, isShowDeploymentName, collectAnnotations, isVivara, model, messages]);

  const onSendLog = useCallback(() => {
    saveConversationHistory();
    // eslint-disable-next-line no-alert
    alert('Saved logs to Kusto');
  }, [saveConversationHistory]);
  const onSendMessage = useCallback(async (v: string) => {
    await sendMessage(v);
  }, [sendMessage]);

  const { accounts } = useMsal();
  const { showNotification } = useNotificationApi();

  const onCopy = useCallback(() => {
    const myName = accounts[0].name?.split(' ')[0];
    const result: IMultiDeploymentChatHistory = getConversaionHistory();
    const texts = [];
    // greeting msg
    if (!_.result(messages[0], 'mine')) {
      texts.push(`Vivara: ${_.result(messages[0], 'content')}`);
    }
    // conversation
    result.conversation.forEach((item) => {
      if (item.user) {
        texts.push(`${myName}: ${item.user}`);
      }
      item.chatbots.forEach((chatbot) => {
        let chatbotText = `Vivara${item.chatbots.length > 1 && chatbot.name ? `(${chatbot.name})` : ''}: ${chatbot.response}`;
        if (chatbot.rate) {
          chatbotText += `\r\nRate: ${chatbot.rate}`;
        }
        if (chatbot.annotation) {
          chatbotText += `\r\nAnnotation: ${chatbot.annotation}`;
        }
        texts.push(chatbotText);
      });
    });
    const text = texts.join('\r\n\r\n');

    navigator.clipboard.writeText(text)
      .then(() => {
        showNotification('Successfully copied to clipboard! 😊 Please paste the text for use.', MessageBarType.success);
      })
      .catch((err) => {
        console.error(err);
        // eslint-disable-next-line max-len
        showNotification('Failed to copy to clipboard. If DevTools opened, pls close it first and have a retry. If it still cannot work, pls contact dev crew.', MessageBarType.error);
      });
  }, [messages]);

  const onClickCopyButton = useCallback(() => {
    // save to kusto
    onSendLog();

    // copy to clipboard
    onCopy();
  }, [onSendLog, onCopy]);

  const onAutoSaveChanged = useCallback((event: React.MouseEvent<HTMLElement>, checked: boolean | undefined) => {
    update({ autoSave: checked });
  }, [update]);

  const onRestartConversation = useCallback(() => {
    if (!autoSave) {
      update({ autoSave: true });

      const autoSaveAlertCount = localStorage.getItem('autoSaveAlertCount') || '0';
      const parsedCount = parseInt(autoSaveAlertCount, 10) || 0;
      if (parsedCount < 3) {
        localStorage.setItem('autoSaveAlertCount', (parsedCount + 1).toString());
        showNotification(
          <div>
            <b>Auto Save is enabled. </b>
            <span>The new conversation is being saved to help improve Vivara. You can turn this off anytime.</span>
          </div>,
          MessageBarType.success,
        );
      }
    }
    restartConversation();
  }, [autoSave, restartConversation, showNotification, update]);

  useEffect(() => {
    if (inviteCode) {
      sendInviteCode(inviteCode);
    }
  }, [inviteCode, sendInviteCode]);

  return (
    isVivara && (!selectedTopic || selectedTopic === Topic.None) ? (
      <WelcomeTopicsPage />
    )
      : (
        <>
          {isVivara && (
            <div
              className={classes.topicTitle}
            >
              <ActionButton
                iconProps={{ iconName: 'Reply' }}
                className="backTopicButton"
                onClick={resetTopics}
              >
                Back
              </ActionButton>
              <div className="topicTitleText">
                <span>{ _.get(TopicList, selectedTopic)?.text }</span>
              </div>
              <TooltipHost
                content={(
                  <div style={{
                    padding: 16,
                    fontFamily: 'Source Serif Pro',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 4,
                    alignSelf: 'stretch',
                  }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 600 }}>Improve the model for everyone</div>
                    <div style={{ fontSize: 12, color: '#7A7A7A', width: '100%' }}>
                      By enabling this option, you allow your current conversation to be saved and used anonymously for analysis and model training, helping Vivara improve for you and all its users. This setting is turned ON for each new conversation by default.
                    </div>
                  </div>
                )}
                directionalHint={DirectionalHint.bottomCenter}
              >
                <Toggle
                  className="autoSaveToggle"
                  label="Auto Save"
                  onText="On"
                  offText="Off"
                  inlineLabel
                  checked={autoSave}
                  onChange={onAutoSaveChanged}
                />
              </TooltipHost>
            </div>
          )}
          <div className={classnames(classes.messageThreadWrapper, isVivara && 'is-vivara')}>
            <MessageThread
              userId="1"
              styles={messageThreadStyles}
              messages={shownMessage}
              onRenderMessage={renderMessage}
              disableJumpToNewMessageButton
              disableEditing
            />
          </div>
          {(model === LLMModel.Companion && !isVivara) && (
            <TodoEditor
              conversationId={conversationId}
              sendButtonDisabled={isPendingUserFeedback}
              onSend={onSendMessage}
              isSidePanelMode
            />
          )}
          <Fireworks isTriggerFireworks={isTriggerFireworks} setIsTriggerFireworks={setIsTriggerFireworks} />
          <Stack
            horizontal
            verticalAlign="center"
            tokens={{ childrenGap: isVivara ? 12 : 6, padding: '10px 20px' }}
            className={classnames(classes.messageBar, isVivara && 'is-vivara', 'message-bar')}
          >
            <StackItem
              className={classes.narrowButtonGroup}
              onClick={onRestartConversation}
            >
              {isVivara ? (
                <div className={classes.restartIconVivara}>
                  {restartIconWithoutBg}
                  <span>New Chat</span>
                </div>
              ) : restartIcon }
            </StackItem>
            <StackItem grow={1} className={isVivara ? classes.sendBoxContainer : ''}>
              <SendBox
                supportNewline
                disabled={isPendingUserFeedback}
                onSendMessage={onSendMessage}
                onTyping={async () => {

                }}
                onRenderIcon={(isHover) => (isVivara ? sendIconVivara(isHover) : sendIcon(isHover))}
                styles={sendBoxStyle(isVivara)}
                strings={{
                  placeholderText: isVivara ? 'Talk with Vivara' : 'Ask me anything ···',
                }}
              />
              {isVivara && (
                <div className={classes.vivaraTaskIcon}>
                  <TooltipHost content="My daily planner">
                    <DailyPlanner
                      onSend={onSendMessage}
                      conversationId={conversationId}
                      sendButtonDisabled={isPendingUserFeedback}
                    />
                  </TooltipHost>
                  <div className="split-line" />
                </div>
              )}
            </StackItem>
            <>
              {model === LLMModel.Companion && (!isVivara || isDevMode) && (
                <StackItem>
                  <PrimaryButton
                    className={classnames(classes.sendLogButton, isVivara && 'is-vivara')}
                    onClick={onSendLog}
                  >
                    Save
                  </PrimaryButton>
                </StackItem>
              )}
              {model === LLMModel.Companion && (
                <StackItem>
                  <PrimaryButton
                    className={classnames(classes.copyHistoryButton, isVivara && 'is-vivara')}
                    onClick={onClickCopyButton}
                  >
                    Copy
                  </PrimaryButton>
                </StackItem>
              )}
              {(isMultiDeploymentExperiment || isDevMode) && (
              <StackItem>
                <DefaultButton
                  className={classnames(classes.toggleDeploymentNameButton, isVivara && 'is-vivara')}
                  onClick={() => setIsShowDeploymentName(!isShowDeploymentName)}
                >
                  {isShowDeploymentName ? 'Hide more' : 'Show more'}
                </DefaultButton>
              </StackItem>
              )}
            </>
          </Stack>
        </>
      )
  );
};
